// Oral potentially malignant disorders & early detection — pre→post educational-intervention KAP instrument.
// Content grounded in WHO 2020/2021 OPMD terminology and mainstream early-detection guidance.
// STARTER DRAFTS pending expert validation.
import { baseDemographics, TF, mc, same } from './common';

const demographics = [
  ...baseDemographics,
  { key: 'A4', field: 'examTraining', type: 'single', stem: 'Have you received formal training in oral mucosal examination?',
    options: same('Yes, hands-on and didactic', 'Didactic only', 'No formal training') },
];

const knowledge = [
  { key: 'KQ1', type: 'single', options: TF,
    stem: 'Leukoplakia is classified as an oral potentially malignant disorder (OPMD).' },
  { key: 'KQ2', type: 'single', options: TF,
    stem: 'Oral submucous fibrosis is strongly associated with areca (betel) nut use.' },
  { key: 'KQ3', type: 'single', options: TF,
    stem: 'Among OPMDs, erythroplakia carries the highest risk of malignant transformation.' },
  { key: 'KQ4', type: 'single', options: TF,
    stem: 'A definitive diagnosis of an OPMD or oral cancer can be made on clinical appearance alone, without histopathology.' },
  { key: 'KQ5', type: 'single', options: TF,
    stem: 'Both smoked and smokeless (chewed) forms of tobacco are risk factors for OPMDs.' },
  { key: 'KQ6', type: 'single', options: TF,
    stem: 'Oral lichen planus is currently included among the oral potentially malignant disorders.' },
  { key: 'KQ7', type: 'single', options: TF,
    stem: 'A persistent oral ulcer or mucosal lesion that has not healed within two weeks warrants referral or biopsy.' },
  { key: 'KQ8', type: 'single', options: TF,
    stem: 'Adjuncts such as toluidine blue staining can replace the need for a tissue biopsy when a lesion is suspicious.' },
  { key: 'KQ9', type: 'single', options: TF,
    stem: 'Early detection of OPMDs and oral cancer is associated with improved prognosis and survival.' },
  { key: 'KQ10', type: 'single', options: mc(
    'Erythroplakia',
    'Frictional keratosis',
    'Geographic tongue',
    'Aphthous ulcer'),
    stem: 'Which of the following lesions has the HIGHEST malignant-transformation potential?' },
  { key: 'KQ11', type: 'single', options: mc(
    'Areca (betel) nut chewing',
    'Drinking fluoridated water',
    'Using a soft toothbrush',
    'Eating citrus fruit'),
    stem: 'Which of the following is the principal risk factor for oral submucous fibrosis?' },
  { key: 'KQ12', type: 'single', options: mc(
    'Incisional biopsy with histopathological examination',
    'Patient-reported symptom history',
    'Toluidine blue staining',
    'Autofluorescence imaging'),
    stem: 'What is the definitive (gold-standard) method to confirm the diagnosis of a suspicious oral lesion?' },
];

// Attitude — 8 items. Reverse-coded (negatively-worded): AQ4, AQ5, AQ6 (handled server-side).
// The "I feel confident…" items moved to the self-efficacy subscale below (v2.0).
const attitude = [
  { key: 'AQ1', type: 'likert',
    stem: 'Screening for OPMDs should be part of every routine dental examination.' },
  { key: 'AQ2', type: 'likert',
    stem: 'Taking a detailed tobacco and areca-nut history is an important part of oral cancer screening.' },
  { key: 'AQ3', type: 'likert',
    stem: 'Promptly referring patients with suspicious or persistent oral lesions is a core responsibility of the dental clinician.' },
  { key: 'AQ4', type: 'likert',
    stem: 'Screening for oral cancer is the responsibility of specialists, not of the general dental practitioner.' },
  { key: 'AQ5', type: 'likert',
    stem: 'Asking patients about their tobacco and areca-nut habits is intrusive and best avoided.' },
  { key: 'AQ6', type: 'likert',
    stem: 'A full oral mucosal examination takes too much time to justify doing it on every patient.' },
  { key: 'AQ7', type: 'likert',
    stem: 'Patients who use tobacco or areca nut should be counselled on cessation as part of their care.' },
  { key: 'AQ8', type: 'likert',
    stem: 'I would welcome additional training in oral mucosal examination and the early detection of OPMDs.' },
];

// Self-efficacy / confidence — 5 items mapped to the topic's core competencies. Summed
// (max 25), no reverse-coding; reported as a separate construct from attitude (v2.0).
const selfEfficacy = [
  { key: 'SE1', type: 'likert',
    stem: 'I feel confident in my ability to recognise an oral potentially malignant disorder clinically.' },
  { key: 'SE2', type: 'likert',
    stem: 'I am comfortable performing or arranging a biopsy when an oral lesion appears suspicious.' },
  { key: 'SE3', type: 'likert',
    stem: 'I feel confident performing a systematic oral mucosal examination and palpating the cervical lymph nodes.' },
  { key: 'SE4', type: 'likert',
    stem: 'I feel confident taking a detailed tobacco and areca-nut/alcohol habit history.' },
  { key: 'SE5', type: 'likert',
    stem: 'I feel confident referring a patient with a persistent or suspicious oral lesion promptly.' },
];

// Practice section, in three parts (v2.0):
//  • PQ1–PQ7 — self-reported frequency (descriptive, NOT scored).
//  • V1–V3   — applied-knowledge vignettes, one guideline-appropriate answer each,
//              scored via the practice key (max 3); answerable at any training stage.
//  • IN1–IN2 — behavioural intention (descriptive Likert), meaningful for pre-clinical
//              respondents for whom current practice does not yet apply.
const practice = [
  { key: 'PQ1', type: 'single',
    stem: 'How often do you perform a systematic oral mucosal examination on new patients?',
    options: same('Always', 'Often', 'Sometimes', 'Rarely', 'Never') },
  { key: 'PQ2', type: 'single',
    stem: 'How often do you palpate the cervical (neck) lymph nodes as part of your examination?',
    options: same('Always', 'Often', 'Sometimes', 'Rarely', 'Never') },
  { key: 'PQ3', type: 'single',
    stem: 'How often do you record a tobacco and areca-nut/alcohol habit history for your patients?',
    options: same('Always', 'Often', 'Sometimes', 'Rarely', 'Never') },
  { key: 'PQ4', type: 'single',
    stem: 'When you encounter a suspicious or persistent (> 2 weeks) oral lesion, how often do you biopsy or refer it?',
    options: same('Always', 'Often', 'Sometimes', 'Rarely', 'Never') },
  { key: 'PQ5', type: 'single',
    stem: 'How often do you photograph or otherwise document oral lesions to monitor them over time?',
    options: same('Always', 'Often', 'Sometimes', 'Rarely', 'Never') },
  { key: 'PQ6', type: 'single',
    stem: 'How often do you provide tobacco/areca-nut cessation advice to patients who use these products?',
    options: same('Always', 'Often', 'Sometimes', 'Rarely', 'Never') },
  { key: 'PQ7', type: 'single',
    stem: 'In the past 12 months, have you referred a patient with a suspicious oral lesion for specialist evaluation?',
    options: same('Yes', 'No', 'Not applicable / no such patient') },
  {
    key: 'V1',
    type: 'single',
    stem: 'A 50-year-old daily areca-nut chewer reports progressively limited mouth opening; the buccal mucosa is pale with palpable fibrous bands. The most appropriate action is:',
    options: same(
      'Recognise likely oral submucous fibrosis (an OPMD); biopsy/refer and advise areca-nut cessation',
      'Reassure — this is normal ageing',
      'Treat empirically as oral thrush',
      'Prescribe an analgesic and review in six months',
    ),
  },
  {
    key: 'V2',
    type: 'single',
    stem: 'An asymptomatic, well-defined red velvety patch is found on the floor of the mouth of a 55-year-old smoker. The most appropriate action is:',
    options: same(
      'Biopsy / refer (erythroplakia carries the highest transformation risk)',
      'Reassure and review in one year',
      'Prescribe a topical steroid',
      'Take no action as it is painless',
    ),
  },
  {
    key: 'V3',
    type: 'single',
    stem: 'A homogeneous white patch that does not rub off has persisted for four weeks despite removing a sharp cusp. The definitive next step to confirm the diagnosis is:',
    options: same(
      'Incisional biopsy with histopathology',
      'Toluidine blue staining alone',
      'A clinical diagnosis without biopsy',
      'A trial of antifungal medication',
    ),
  },
  {
    key: 'IN1',
    type: 'likert',
    stem: 'When I begin clinical practice, I intend to perform a systematic oral mucosal examination on every new patient.',
  },
  {
    key: 'IN2',
    type: 'likert',
    stem: 'I intend to refer or biopsy any oral lesion persisting beyond two weeks.',
  },
];

const education = [
  {
    id: 'opmd-what',
    title: 'What counts as an OPMD',
    points: [
      'Oral potentially malignant disorders (OPMDs) include leukoplakia, erythroplakia, oral submucous fibrosis and oral lichen planus, among others (WHO 2020).',
      'Of these, erythroplakia carries the highest risk of malignant transformation.',
      'Oral submucous fibrosis is strongly linked to areca (betel) nut use — a major issue in the Indian population.',
    ],
    tip: 'Red beats white for danger: erythroplakia (the “e” = evil) transforms far more often than leukoplakia.',
  },
  {
    id: 'opmd-risk',
    title: 'Know the risk factors',
    points: [
      'Both smoked and smokeless (chewed) tobacco are risk factors, as are areca nut and alcohol.',
      'Take a tobacco, areca-nut and alcohol history routinely, and counsel users on cessation.',
    ],
    tip: 'Smoke it or chew it — both bite. Always take the tobacco + areca/“paan” + alcohol history.',
  },
  {
    id: 'opmd-examine',
    title: 'Examine, and act on the two-week rule',
    points: [
      'Perform a systematic oral mucosal examination and palpate the neck nodes on every patient.',
      'Any ulcer or mucosal lesion that has not healed within two weeks warrants referral or biopsy.',
    ],
    tip: 'The 2-week rule: an ulcer not healed in two weeks → refer or biopsy.',
  },
  {
    id: 'opmd-diagnosis',
    title: 'Diagnosis needs histopathology',
    points: [
      'You cannot diagnose an OPMD or oral cancer on clinical appearance alone.',
      'Adjuncts such as toluidine blue staining or autofluorescence can guide you but do NOT replace a tissue biopsy.',
      'Incisional biopsy with histopathological examination is the gold standard — and early detection improves prognosis and survival.',
    ],
    tip: 'Eyes suspect, the biopsy confirms: toluidine blue/light only guide — histopathology diagnoses.',
  },
];

export default {
  slug: 'opmd',
  code: 'KS04',
  title: 'Effect of a Focused Educational Intervention on Knowledge, Attitude, and Practice in the Early Detection of Oral Potentially Malignant Disorders Among Dental Undergraduates and Interns: A Pre–Post Interventional Study',
  shortTitle: 'OPMD & Early Detection',
  studyTitle: 'KAP on Oral Potentially Malignant Disorders & Early Detection',
  topic: 'Oral oncology & screening',
  emoji: '🔬',
  accent: '#FFB020',
  estMinutes: 12,
  mode: 'pre-post',
  status: 'live',
  version: '2.0',
  practiceMax: 3, // scored practice = the three applied vignettes (V1–V3); PQ*/IN* are descriptive
  blurb: 'Recognition of oral potentially malignant disorders, screening attitudes, and early-detection practice — with a built-in learning module that measures what changes.',
  sections: { demographics, knowledge, attitude, selfEfficacy, practice },
  education,
  educationTitle: 'Spotting oral potentially malignant disorders early',
  educationBlurb: 'A two-minute refresher based on the WHO 2020 OPMD classification and early-detection guidance. Read through, then continue to the short follow-up.',
};
